import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Search } from '@mui/icons-material';
import { TextField, InputAdornment } from '@mui/material';
import welfareService from '../utils/welfareService';
import ServiceCard from '../components/ServiceCard';
import Loader from '../components/Loader';
import { snackbar } from '../utils/showMessage';

const textFieldStyle = {
    mb: 2.5,
    '& .MuiOutlinedInput-root': {
        borderRadius: '15px',
        backgroundColor: '#fff',
        '& fieldset': {
            borderColor: 'rgba(0, 0, 0, 0.1)',
        },
        '&:hover fieldset': {
            borderColor: 'rgba(0,0,0,0.2)',
        },
        '&.Mui-focused fieldset': {
            borderColor: '#007bff',
        },
    }
};

const applySearchFilter = (serviceList, query) => {
    if (!query || query.length < 2) {
        return serviceList;
    }
    const normalizedQuery = query.toLowerCase();
    return serviceList.filter((service) =>
        (service.title || '').toLowerCase().includes(normalizedQuery) ||
        (service.description || '').toLowerCase().includes(normalizedQuery)
    );
};

const ServicesList = () => {
    const { id } = useParams();
    const location = useLocation();
    const searchParams = new URLSearchParams(window.location.search);
    const subParam = searchParams.get('sub');
    const navigate = useNavigate();
    const storageKey = `services-list:${id}`;
    const readSavedSearchQuery = () => {
        if (typeof window === 'undefined') return '';
        try {
            const raw = sessionStorage.getItem(storageKey);
            if (!raw) return '';
            const parsed = JSON.parse(raw);
            return parsed?.searchQuery ?? '';
        } catch (error) {
            console.error('Error reading saved search query:', error);
            return '';
        }
    };
    const [category, setCategory] = useState(null);
    const [services, setServices] = useState([]);
    const [filteredServices, setFilteredServices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState(() => readSavedSearchQuery());

    useEffect(() => {
        loadCategoryAndServices();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    useEffect(() => {
        setSearchQuery(readSavedSearchQuery());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [storageKey]);

    useEffect(() => {
        setFilteredServices(applySearchFilter(services, searchQuery));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [services, searchQuery]);

    const loadCategoryAndServices = async () => {
        try {
            setLoading(true);
            const categoryData = await welfareService.getCategoryById(id);
            setCategory(categoryData);

            // اگر sub در url هست و مقدارش all نیست، با subCategoryId فیلتر کن
            let servicesData = [];
            if (subParam && subParam !== 'all') {
                servicesData = await welfareService.getServices(categoryData._id, null, subParam);
            } else {
                servicesData = await welfareService.getServices(categoryData._id);
            }
            setServices(servicesData);
        } catch (error) {
            console.error('Error loading category:', error);
            snackbar('خطا در بارگذاری اطلاعات');
            navigate('/');
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = (e) => {
        const query = e.target.value;
        setSearchQuery(query);
        persistSearchQuery(query);
    };

    const persistSearchQuery = (value) => {
        if (typeof window === 'undefined') return;
        try {
            sessionStorage.setItem(storageKey, JSON.stringify({ searchQuery: value }));
        } catch (error) {
            console.error('Error persisting search query:', error);
        }
    };

    const serviceLinkState = {
        fromServicesList: true,
        listPath: `${location.pathname}${location.search}`,
        searchQuery,
    };

    if (loading) {
        return <Loader />;
    }

    if (!category) {
        return null;
    }

    return (
        <div className="main-container container">


            {/* Search */}
            <div className="row mb-1">
                <div className="col-12">
                    <TextField
                        fullWidth
                        label={'جستجو در ' + category.title}
                        placeholder={'جستجو در ' + category.title + '...'}
                        value={searchQuery}
                        onChange={handleSearch}
                        sx={textFieldStyle}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Search color="action" />
                                </InputAdornment>
                            ),
                        }}
                    />
                </div>
            </div>

            {/* Services Grid */}
            <div className="row mb-2"> 
                {filteredServices.length > 0 ? (
                    filteredServices.map((service) => (
                        <ServiceCard key={service._id} service={service} linkState={serviceLinkState} />
                    ))
                ) : (
                    <div className="col-12 text-center py-5">
                        <p className="text-muted">خدمتی در این دسته‌بندی یافت نشد</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ServicesList;
